import { useState } from "react"

import {
  useReportsTransportInvalidation,
  useSaveReportTransport,
  useUpdateReportTransport,
  validateReportName,
} from "@/reports"
import {
  Button,
  focusOnMount,
  FormField,
  getUserFacingErrorMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { SaveResourceButton } from "@/components/ResourceActionButton"
import type { MetricViews } from "@/types/MetricViewConfig"

interface SaveMultiRunReportButtonProps {
  runIds: string[]
  simulationOutputIds?: string[]
  viewConfig: Record<string, unknown>
  metricConfig?: MetricViews
  existingReportId?: string | null
  onSaved?: (reportId: string) => void
  isDisabled?: boolean
}

const log = logger("SaveMultiRunReportButton")

export const SaveMultiRunReportButton: React.FC<
  SaveMultiRunReportButtonProps
> = ({
  runIds,
  simulationOutputIds = [],
  viewConfig,
  metricConfig,
  existingReportId,
  onSaved,
  isDisabled,
}) => {
  const { trackEvent } = useAnalytics()
  const [displayName, setDisplayName] = useState("")
  const [validationAttempted, setValidationAttempted] = useState(false)
  const nameValidation = validateReportName(displayName)
  const [isOpen, setIsOpen] = useState(false)
  const [savedId, setSavedId] = useState<string | null>(null)

  const saveMutation = useSaveReportTransport()
  const updateMutation = useUpdateReportTransport()
  const reportsInvalidation = useReportsTransportInvalidation()
  const isLoading = saveMutation.isPending || updateMutation.isPending

  const invalidateReportCaches = async (reportId: string) => {
    try {
      await Promise.all([
        reportsInvalidation.invalidateReport(reportId),
        reportsInvalidation.invalidateList(),
      ])
    } catch (error) {
      log.error("Failed to invalidate report caches", error)
    }
  }

  const handleSave = async () => {
    if (
      (runIds.length === 0 && simulationOutputIds.length === 0) ||
      isLoading
    ) {
      return
    }

    if (!existingReportId && nameValidation.error) {
      setValidationAttempted(true)
      return
    }

    try {
      if (existingReportId) {
        await updateMutation.mutateAsync({
          report_id: existingReportId,
          run_ids: runIds,
          ...(simulationOutputIds.length > 0 && {
            simulation_output_ids: simulationOutputIds,
          }),
          view_config: viewConfig,
          metric_config: metricConfig,
        })
        await invalidateReportCaches(existingReportId)
        trackEvent(TRACKING_EVENTS.REPORTS.SAVED, {
          report_id: existingReportId,
          run_count: runIds.length,
          is_update: true,
        })
        onSaved?.(existingReportId)
      } else {
        const result = await saveMutation.mutateAsync({
          display_name: nameValidation.name,
          run_ids: runIds,
          ...(simulationOutputIds.length > 0 && {
            simulation_output_ids: simulationOutputIds,
          }),
          view_config: viewConfig,
          metric_config: metricConfig,
        })
        await invalidateReportCaches(result.ulid)
        trackEvent(TRACKING_EVENTS.REPORTS.SAVED, {
          report_id: result.ulid,
          run_count: runIds.length,
          is_update: false,
        })
        setSavedId(result.ulid)
        onSaved?.(result.ulid)
      }
    } catch (error) {
      log.error("Failed to save report", error)
      toast.danger({
        title: "Error",
        description: getUserFacingErrorMessage(error, {
          fallback: "Failed to save report.",
        }),
      })
    }
  }

  // For existing reports, just a direct save button (no popover)
  if (existingReportId) {
    return (
      <Button
        variant="secondary"
        aria-label="Save report"
        data-testid="save-report-button"
        onClick={() => {
          void handleSave()
        }}
        disabled={isLoading || isDisabled}
      >
        {isLoading ? "Saving…" : "Save"}
      </Button>
    )
  }

  // For new reports, show popover with name input
  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          setSavedId(null)
          setValidationAttempted(false)
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          aria-label="Save report"
          data-testid="save-report-button"
        >
          Save
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" className="p-4 gap-3 w-80">
        <div className="flex flex-col w-full gap-3">
          {savedId ? (
            <div
              className="text-sm text-coval-success-text"
              data-testid="save-report-success"
            >
              Report saved!
            </div>
          ) : (
            <>
              <div className="text-sm font-medium">Save Report</div>
              <FormField
                label="Report name"
                error={validationAttempted ? nameValidation.error : undefined}
              >
                {(fieldProps) => (
                  <Input
                    {...fieldProps}
                    placeholder="Report name"
                    data-testid="save-report-name-input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        void handleSave()
                      }
                    }}
                    ref={focusOnMount}
                    disabled={isLoading}
                  />
                )}
              </FormField>
              <SaveResourceButton
                resourceName="report"
                data-testid="save-report-confirm-button"
                onClick={() => {
                  void handleSave()
                }}
                validationIssues={[nameValidation.error]}
                onValidationAttempt={() => setValidationAttempted(true)}
                loading={isLoading}
                disabled={
                  isLoading ||
                  (runIds.length === 0 && simulationOutputIds.length === 0)
                }
              />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
